import { HttpClient } from '@angular/common/http';
import { inject, Service } from '@angular/core';

import { API_URL } from '@constants';
import { AnyResource, PermissionLevel } from '@models';

export interface Config {
  maxLifetime: number;
  permissions: {
    selection: {
      items: Array<{
        rid: number;
        type: number;
        permission: number;
        displayName: string;
      }>;
    };
  };
}

@Service()
export class ResourceOperationsService {
  http = inject(HttpClient);

  copyResourceByPath = <T = AnyResource>(
    path: string,
    body: Record<string, unknown>
  ) => this.http.post<T>(`${API_URL}/${path}`, body);

  deleteResourceByPath = (path: string) =>
    this.http.delete(`${API_URL}/${path}`);

  moveResourceByPath = <T = AnyResource>(
    path: string,
    body: Record<string, unknown>
  ) => this.http.put<T>(`${API_URL}/${path}`, body);

  getRoomConfig = (pid: string | number) =>
    this.http.get<Config>(`${API_URL}/rooms/${pid}/config`);

  createResource = (urlPath: string, body: Record<string, unknown>) =>
    this.http.post(`${API_URL}/${urlPath}`, body);

  getResource = <T = unknown>(url: string) =>
    this.http.get<T>(`${API_URL}/${url}`);

  getResourcesWithParams = <T = unknown>(
    url: string,
    params: Record<string, string | number | boolean>
  ) =>
    this.http.get<T[]>(`${API_URL}/${url}`, {
      observe: 'response',
      params,
    });

  updateApprovals = <T extends object>(
    resourceType: string,
    rid: number,
    body: T
  ) =>
    this.http.put<{ updateDate: number; status: number }>(
      `${API_URL}/${resourceType}/${rid}/approvals`,
      body
    );

  updateGuestAccess = (
    resourceType: string,
    rid: number,
    body: {
      guest: Record<string, unknown>;
      permission: { level: PermissionLevel; inheritance?: number };
    }
  ) =>
    this.http.put<{ updateDate: number }>(
      `${API_URL}/${resourceType}/${rid}/guestaccess`,
      body
    );

  postNotificationText = (path: string, body: Record<string, unknown>) =>
    this.http.post(`${API_URL}/${path}/notificationtext`, body);

  updateResource = <T = unknown>(path: string, body: Record<string, unknown>) =>
    this.http.post<T>(`${API_URL}/${path}`, body);

  signFileWithUrl = (url: string, body: Record<string, unknown>) =>
    this.http.post<{ updateDate: number; rid?: number; address?: string }>(
      url,
      body
    );
}
